import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Flex, Heading, Text, Icon, Spinner, Grid,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, PopoverHeader,
} from '@chakra-ui/react';
import { FiVideo } from 'react-icons/fi';
import ReactPaginate from 'react-paginate';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import useAuth from 'hooks/useAuth';
import { ZoomKpiRow, ZoomLogFilters, ZoomLogTable, ZoomLogDetailModal } from 'components/monitoring';
import {
  getRecordingLogs,
  getRecordingsSummary,
  getZoomAccounts,
} from 'api/administrador/monitoring/zoomRecordings';

dayjs.extend(relativeTime);
dayjs.locale('es');

const ADMIN_ROLES = ['admin', 'supervisor_admin'];
const PAGE_SIZE = 20;

const DATE_RANGE_OPTIONS = [
  { value: 'today', label: 'Hoy' },
  { value: '7', label: '7 días' },
  { value: '30', label: '30 días' },
  { value: 'all', label: 'Todo' },
];

/**
 * Chip "X cuentas activas" con popover al hover que lista todas las cuentas
 * Zoom activas de la org. Cada cuenta muestra email + un dot verde si ya
 * emitió una grabación hoy, gris si no.
 *
 * `accounts` viene de GET /api/zoom/accounts (cache 5 min server-side).
 */
function ZoomAccountsChip({ accounts, loading }) {
  const total = accounts?.total ?? 0;
  const activeToday = accounts?.active_today ?? 0;
  const users = accounts?.users || [];

  if (!accounts && !loading) return null;

  return (
    <Popover trigger="hover" placement="bottom-end" openDelay={100} closeDelay={150}>
      <PopoverTrigger>
        <Box
          bg="rgba(255,255,255,.14)"
          px={3}
          py={2}
          borderRadius="10px"
          fontSize="12px"
          whiteSpace="nowrap"
          cursor="pointer"
          _hover={{ bg: 'rgba(255,255,255,.22)' }}
          transition="background .15s"
        >
          {loading ? (
            <Flex align="center" gap={2}>
              <Spinner size="xs" color="white" />
              <span>cargando cuentas…</span>
            </Flex>
          ) : (
            <>
              {total} cuentas activas
              {activeToday > 0 && (
                <Box as="span" opacity={0.75} ml={1}>· {activeToday} con clase hoy</Box>
              )}
            </>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent maxW="360px" color="gray.800" bg="white" boxShadow="lg" borderColor="gray.200">
        <PopoverArrow />
        <PopoverHeader fontWeight="600" fontSize="sm" borderBottomColor="gray.200">
          Cuentas Zoom · {total} activas
        </PopoverHeader>
        <PopoverBody maxH="360px" overflowY="auto" p={2}>
          {users.length === 0 && (
            <Text fontSize="xs" color="gray.500" px={2} py={3}>
              Sin cuentas activas en tu org Zoom.
            </Text>
          )}
          {users.map((u) => (
            <Flex
              key={u.id}
              align="center"
              gap={2}
              px={2}
              py={1.5}
              borderRadius="6px"
              _hover={{ bg: 'gray.50' }}
            >
              <Box
                w="8px"
                h="8px"
                borderRadius="999px"
                bg={u.has_recording_today ? '#10B981' : '#CBD5E0'}
                flexShrink={0}
              />
              <Text fontSize="xs" color="gray.700" isTruncated title={u.email}>
                {u.email}
              </Text>
            </Flex>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Indicador "Webhook activo · última: hace X min" que aparece en el header.
 * Toma el createdAt del log más reciente. Si el más reciente tiene < 24 h,
 * consideramos el webhook "activo".
 */
function WebhookIndicator({ logs }) {
  const latest = logs?.[0]?.createdAt;
  const isActive = latest ? dayjs().diff(dayjs(latest), 'hour') < 24 : false;
  const label = latest ? dayjs(latest).fromNow() : 'sin datos aún';
  return (
    <Box
      bg="rgba(255,255,255,.14)"
      px={3}
      py={2}
      borderRadius="10px"
      fontSize="12px"
      display="flex"
      alignItems="center"
      gap="8px"
      whiteSpace="nowrap"
    >
      <Box w="8px" h="8px" borderRadius="999px" bg={isActive ? '#4ADE80' : '#F87171'} />
      Webhook {isActive ? 'activo' : 'inactivo'} · última: {label}
    </Box>
  );
}

function rangeFromFilter(dateRange) {
  const now = dayjs();
  if (dateRange === 'today') {
    return { dateFrom: now.startOf('day').toISOString(), dateTo: now.endOf('day').toISOString() };
  }
  if (dateRange === '7') {
    return { dateFrom: now.subtract(7, 'day').startOf('day').toISOString(), dateTo: now.endOf('day').toISOString() };
  }
  if (dateRange === '30') {
    return { dateFrom: now.subtract(30, 'day').startOf('day').toISOString(), dateTo: now.endOf('day').toISOString() };
  }
  return { dateFrom: '', dateTo: '' };
}

export default function MonitoringClasesGrabadas() {
  const { user } = useAuth();

  // Role gate: only admin + supervisor_admin (defensa en profundidad; el backend también gatea).
  const isAllowed = ADMIN_ROLES.includes(user?.role?.type);

  const [filters, setFilters] = useState({
    status: '',
    dateRange: 'today',
    search: '',
    page: 1,
  });
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageCount: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const { dateFrom, dateTo } = useMemo(() => rangeFromFilter(filters.dateRange), [filters.dateRange]);

  const loadLogs = useCallback(async () => {
    if (!isAllowed) return;
    try {
      setLoading(true);
      const res = await getRecordingLogs({
        page: filters.page,
        pageSize: PAGE_SIZE,
        status: filters.status,
        dateFrom,
        dateTo,
        search: filters.search,
      });
      setLogs(res?.data || []);
      setPagination({
        page: res?.meta?.pagination?.page || 1,
        pageCount: res?.meta?.pagination?.pageCount || 0,
        total: res?.meta?.pagination?.total || 0,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[monitoring] error cargando logs:', e);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [isAllowed, filters.page, filters.status, filters.search, dateFrom, dateTo]);

  const loadSummary = useCallback(async () => {
    if (!isAllowed) return;
    try {
      setSummaryLoading(true);
      // Los KPIs usan el mismo rango de fechas que la tabla.
      const res = await getRecordingsSummary({ dateFrom, dateTo, all: !dateFrom && !dateTo });
      setSummary(res || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[monitoring] error cargando summary:', e);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [isAllowed, dateFrom, dateTo]);

  const loadAccounts = useCallback(async () => {
    if (!isAllowed) return;
    try {
      setAccountsLoading(true);
      const res = await getZoomAccounts();
      setAccounts(res || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[monitoring] error cargando cuentas Zoom:', e);
      setAccounts(null);
    } finally {
      setAccountsLoading(false);
    }
  }, [isAllowed]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handlePageChange = ({ selected }) => setFilters({ ...filters, page: selected + 1 });
  const handleViewDetail = (log) => { setSelectedLogId(log.id); setIsDetailOpen(true); };

  if (!isAllowed) {
    return (
      <Box p={8}>
        <Text color="gray.500">Esta vista está disponible solo para administradores.</Text>
      </Box>
    );
  }

  return (
    <Box p={{ base: 3, md: 6 }}>
      {/* Header banner degradado — mismo lenguaje visual que Auditoría */}
      <Box
        borderRadius="16px"
        p={{ base: 4, md: 6 }}
        mb={5}
        color="white"
        bgGradient="linear(135deg, #6D48E5 0%, #8B5CF6 55%, #A855F7 100%)"
      >
        <Flex align="center" gap={4}>
          <Flex w="48px" h="48px" borderRadius="12px" bg="rgba(255,255,255,.15)" align="center" justify="center">
            <Icon as={FiVideo} boxSize={6} />
          </Flex>
          <Box flex={1}>
            <Text fontSize="11px" textTransform="uppercase" letterSpacing=".08em" opacity={0.85} fontWeight="600">
              Monitoring · proceso Zoom → S3
            </Text>
            <Heading fontSize="22px" fontWeight="700" letterSpacing="-.01em" mt="2px">
              Clases grabadas
            </Heading>
          </Box>
          <WebhookIndicator logs={logs} />
          <ZoomAccountsChip accounts={accounts} loading={accountsLoading} />
        </Flex>
      </Box>

      {/* Periodo: gobierna KPIs + tabla, por eso va arriba de ambos */}
      <Flex align="center" gap={3} mb={3} wrap="wrap">
        <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase" color="gray.500">
          Periodo
        </Text>
        <Flex bg="white" border="1px solid" borderColor="gray.200" borderRadius="10px" p="3px" gap="2px">
          {DATE_RANGE_OPTIONS.map((o) => {
            const active = filters.dateRange === o.value;
            return (
              <Box
                key={o.value}
                as="button"
                type="button"
                onClick={() => setFilters({ ...filters, dateRange: o.value, page: 1 })}
                px={3}
                py="6px"
                borderRadius="8px"
                fontSize="13px"
                fontWeight={active ? '600' : '500'}
                bg={active ? '#6D48E5' : 'transparent'}
                color={active ? 'white' : 'gray.600'}
                _hover={active ? {} : { bg: 'gray.100' }}
                transition="background .15s"
                aria-pressed={active}
              >
                {o.label}
              </Box>
            );
          })}
        </Flex>
      </Flex>

      <ZoomKpiRow summary={summary} loading={summaryLoading} />

      <Box bg="white" borderRadius="14px" border="1px solid" borderColor="gray.200" p={{ base: 3, md: 5 }}>
        <ZoomLogFilters filters={filters} onChange={setFilters} />
        <ZoomLogTable logs={logs} loading={loading} onViewDetail={handleViewDetail} />

        {pagination.total > 0 && (
          <Flex
            mt={4} pt={4}
            borderTopWidth="1px" borderColor="gray.100"
            justify="space-between" align="center" wrap="wrap" gap={3}
          >
            <Text color="gray.600" fontSize="sm">
              Mostrando {(pagination.page - 1) * PAGE_SIZE + 1}-
              {Math.min(pagination.page * PAGE_SIZE, pagination.total)} de {pagination.total} grabaciones
            </Text>
            {pagination.pageCount > 1 && (
              <ReactPaginate
                breakLabel="..."
                nextLabel=">"
                previousLabel="<"
                onPageChange={handlePageChange}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                pageCount={pagination.pageCount}
                forcePage={pagination.page - 1}
                renderOnZeroPageCount={null}
                containerClassName="zoomLogsPagination"
                pageClassName="zoomLogsPaginationItem"
                breakClassName="zoomLogsPaginationBreak"
                activeClassName="zoomLogsPaginationActive"
                previousClassName="zoomLogsPaginationItem"
                nextClassName="zoomLogsPaginationItem"
                disabledClassName="zoomLogsPaginationDisabled"
              />
            )}
          </Flex>
        )}
      </Box>

      {/* Mismo look que la paginación de Reporte de Pagos */}
      <style>{`
        .zoomLogsPagination { display: flex; align-items: center; list-style: none; gap: 4px; margin: 0; padding: 0; }
        .zoomLogsPaginationItem {
          display: flex; align-items: center; justify-content: center;
          min-width: 36px; height: 36px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          cursor: pointer; font-size: 14px; color: #4a5568; transition: all 0.2s;
        }
        .zoomLogsPaginationItem:hover { background-color: #f7fafc; border-color: #cbd5e0; }
        .zoomLogsPaginationItem a {
          display: flex; align-items: center; justify-content: center;
          width: 100%; height: 100%; padding: 0 12px;
        }
        .zoomLogsPaginationBreak { padding: 0 4px; color: #a0aec0; }
        .zoomLogsPaginationActive { background-color: #574feb !important; border-color: #574feb !important; color: white !important; }
        .zoomLogsPaginationActive a { color: white !important; }
        .zoomLogsPaginationDisabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>

      <ZoomLogDetailModal
        isOpen={isDetailOpen}
        setIsOpen={(v) => { setIsDetailOpen(v); if (!v) setSelectedLogId(null); }}
        logId={selectedLogId}
        onRetried={() => { loadLogs(); loadSummary(); }}
      />
    </Box>
  );
}
